import React, { useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  SparklesIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import WhatsAppAgentConfig from './WhatsAppAgentConfig';

/**
 * Standalone WhatsApp Agent screening creation modal — opened from the "WhatsApp Agent"
 * button on the Job Screenings page.
 *
 * Creates a new screening with `includeWhatsAppAgent: true` via the existing
 * POST /api/screenings/job/:jobId endpoint.
 *
 * Contains:
 *  1. Screening name input
 *  2. Resume collection toggle (with AI criteria generation when enabled)
 *  3. WhatsApp interview questions (reuses WhatsAppAgentConfig)
 */
export default function WhatsAppAgentModal({ isOpen, onClose, jobId, onSaved }) {
  const [saving, setSaving] = useState(false);

  // Screening config state
  const [screeningName, setScreeningName] = useState('');
  const [includeResume, setIncludeResume] = useState(false);
  const [resumeCriteria, setResumeCriteria] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [generatingCriteria, setGeneratingCriteria] = useState(false);

  const handleGenerateCriteria = useCallback(async () => {
    if (!jobId) {
      toast.error('Job ID is required to generate criteria');
      return;
    }
    setGeneratingCriteria(true);
    try {
      const res = await axios.post('/api/screenings/generate-criteria-preview', { jobId });
      const generated = Array.isArray(res.data?.criteria) ? res.data.criteria : [];
      if (generated.length === 0) {
        toast.error('No criteria were generated. Try again or add manually.');
        return;
      }
      setResumeCriteria(generated);
      toast.success(`Generated ${generated.length} criteria`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate criteria');
    } finally {
      setGeneratingCriteria(false);
    }
  }, [jobId]);

  const handleSave = async () => {
    if (!screeningName.trim()) {
      toast.error('Please enter a screening name');
      return;
    }
    if (questions.length === 0) {
      toast.error('Please add at least one WhatsApp interview question');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`/api/screenings/job/${jobId}`, {
        name: screeningName.trim(),
        hasResumeScoring: includeResume,
        hasAssessment: false,
        hasInterview: false,
        includeDocumentCollection: false,
        includeWhatsAppAgent: true,
        whatsAppAgentQuestions: questions,
        resumeScoringCriteria: includeResume ? resumeCriteria : undefined,
      });
      toast.success('WhatsApp Agent screening created');
      // Reset
      setScreeningName('');
      setIncludeResume(false);
      setResumeCriteria([]);
      setQuestions([]);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create screening');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-fadeIn mx-4">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="p-2 rounded-xl bg-[#25D366]/10 text-[#25D366]">
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900">WhatsApp Agent</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Create a screening that interviews candidates over WhatsApp
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Screening name */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">
              Screening Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={screeningName}
              onChange={(e) => setScreeningName(e.target.value)}
              placeholder="e.g. WhatsApp Pre-Screen Round"
              data-testid="whatsapp-screening-name"
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#25D366]/40 focus:border-[#25D366] transition-all"
            />
          </div>

          {/* Resume toggle */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 bg-gray-50/50">
              <div className="flex items-center gap-3">
                <DocumentArrowUpIcon className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Collect Resume via WhatsApp
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Ask candidates to upload their resume before the interview starts
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={includeResume}
                data-testid="whatsapp-resume-toggle"
                onClick={() => {
                  setIncludeResume(!includeResume);
                  if (includeResume) {
                    setResumeCriteria([]);
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400/40 ${
                  includeResume ? 'bg-[#007BFF]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    includeResume ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Criteria section when resume is on */}
            {includeResume && (
              <div className="p-4 border-t border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700">
                    Resume Scoring Criteria
                    {resumeCriteria.length > 0 && (
                      <span className="text-gray-400 font-normal ml-1.5">
                        ({resumeCriteria.length}/8)
                      </span>
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={handleGenerateCriteria}
                    disabled={generatingCriteria}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-[#007BFF]/40 bg-[#007BFF]/5 text-xs font-semibold text-[#007BFF] hover:bg-[#007BFF]/10 hover:border-[#007BFF]/60 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {generatingCriteria ? (
                      <>
                        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#007BFF]/60 border-t-transparent" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-3.5 w-3.5" />
                        {resumeCriteria.length > 0 ? 'Regenerate' : 'Generate with AI'}
                      </>
                    )}
                  </button>
                </div>

                {resumeCriteria.length > 0 ? (
                  <div className="space-y-1.5">
                    {resumeCriteria.map((c, idx) => (
                      <div
                        key={c.id || idx}
                        className="flex items-start gap-2 px-3 py-2 rounded-lg bg-white border border-gray-100"
                      >
                        <span className="text-[10px] font-bold text-gray-400 mt-0.5 shrink-0">
                          {idx + 1}
                        </span>
                        <p className="text-xs text-gray-700 flex-1">{c.text}</p>
                        <button
                          type="button"
                          onClick={() =>
                            setResumeCriteria((prev) =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                          className="p-0.5 text-gray-300 hover:text-red-500 transition-colors shrink-0"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 text-center py-3">
                    No criteria yet — click "Generate with AI" to auto-create from the job description.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Questions */}
          <WhatsAppAgentConfig
            jobId={jobId}
            questions={questions}
            onChange={setQuestions}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50/50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !screeningName.trim() || questions.length === 0}
            data-testid="whatsapp-agent-save-btn"
            className="px-5 py-2 text-sm font-semibold text-white bg-[#25D366] hover:bg-[#1c9950] rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
          >
            {saving ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-transparent" />
                Creating…
              </>
            ) : (
              'Create Screening'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
